import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../shared/auth/AuthContext';
import { apiClient } from '../../shared/api/client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  LoadingSkeleton,
  Modal,
} from '@nexora/design-system';

/* ── Contratos reais ──
 * GET /logistics/mdfes  → { data: MdfeDto[], total }
 * POST /logistics/mdfes → MdfeDto (body: { number?, driverName?, vehiclePlate?,
 *   route?, ctes?: string[], emitir? })
 */

interface MdfeDto {
  id: string;
  number: string;
  serie?: string | null;
  accessKey?: string | null;
  status: string;
  driverName?: string | null;
  vehiclePlate?: string | null;
  route?: string | null;
  ctes?: unknown[] | null;
  issuedAt?: string | null;
}

const STATUS_VARIANT: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'processing' | 'critical'> = {
  DRAFT: 'neutral',
  EMITIDO: 'info',
  EM_TRANSITO: 'processing',
  ENCERRADO: 'neutral',
  CANCELADO: 'error',
};

export function MdfesPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ driverName: '', vehiclePlate: '', route: '', emitir: true });

  const listQ = useQuery({
    queryKey: ['logistics', 'mdfes'],
    queryFn: async () => {
      const data = await apiClient.get<any>('/logistics/mdfes', { params: { page: '1', limit: '100' } });
      return data as { data: MdfeDto[] };
    },
    enabled: !!tenantId,
  });

  const create = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post('/logistics/mdfes', {
        driverName: form.driverName || undefined,
        vehiclePlate: form.vehiclePlate || undefined,
        route: form.route || undefined,
        emitir: form.emitir,
      });
      return res;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['logistics', 'mdfes'] });
      setModalOpen(false);
      setForm({ driverName: '', vehiclePlate: '', route: '', emitir: true });
    },
  });

  const items = listQ.data?.data ?? [];

  return (
    <Card variant="outlined" padding="0" className="page-card">
      <CardHeader className="px-md pt-md" action={<Button onClick={() => setModalOpen(true)}>Novo MDF-e</Button>}>
        MDF-e Management — Manifestos Eletrônicos de Documentos Fiscais
      </CardHeader>
      <CardBody>
        {listQ.isLoading ? (
          <LoadingSkeleton />
        ) : items.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--on-surface-variant)' }}>
            Nenhum MDF-e cadastrado.
          </div>
        ) : (
          <table className="ds-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Access Key</th>
                <th>Driver</th>
                <th>Vehicle Plate</th>
                <th>Route (Orig → Dest)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id}>
                  <td style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.8rem' }}>
                    {m.accessKey ?? `${m.number}${m.serie ? ` / ${m.serie}` : ''}`}
                  </td>
                  <td>{m.driverName ?? '—'}</td>
                  <td style={{ fontFamily: "'JetBrains Mono', monospace" }}>{m.vehiclePlate ?? '—'}</td>
                  <td>{m.route ?? '—'}</td>
                  <td><Badge variant={STATUS_VARIANT[m.status] ?? 'neutral'} size="sm">{m.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Novo MDF-e">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
          style={{ display: 'grid', gap: '1rem' }}
        >
          <Input label="Motorista (Driver)" value={form.driverName} onChange={(e) => setForm((f) => ({ ...f, driverName: e.target.value }))} />
          <Input label="Placa do Veículo" value={form.vehiclePlate} onChange={(e) => setForm((f) => ({ ...f, vehiclePlate: e.target.value }))} />
          <Input label="Rota (Orig → Dest)" value={form.route} onChange={(e) => setForm((f) => ({ ...f, route: e.target.value }))} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.emitir} onChange={(e) => setForm((f) => ({ ...f, emitir: e.target.checked }))} />
            Emitir imediatamente
          </label>
          {create.isError && <div role="alert" style={{ color: 'var(--error)' }}>Erro ao criar MDF-e.</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit" disabled={create.isPending}>{create.isPending ? 'Criando…' : 'Criar'}</Button>
          </div>
        </form>
      </Modal>
    </Card>
  );
}